import 'keen-slider/keen-slider.min.css'

import styled from '@emotion/styled'
import { AnimatedLine } from 'app/components/Common/Animation/AnimatedLine'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { DropDown } from 'app/components/Common/Animation/DropDown'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Dot } from 'app/components/Common/Dot'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ControlRight } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { positionStyle } from 'app/utils/customFunctions'
import { useParallax } from 'app/utils/customFunctions'
import { AnimatePresence, motion, useScroll } from 'framer-motion'
import { useKeenSlider } from 'keen-slider/react'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  cta?: ButtonProps
  description?: string
  images?: ImageProps[]
  subtitle?: string
  title?: string
}

export const RoomsPreview = memo(function RoomsPreview({
  cta,
  description,
  images,
  subtitle,
  title,
}: Props) {
  if (!images) {
    return null
  }

  if (images.length < 1) {
    return null
  }

  const containerRef = useRef<any>()

  const AUTOPLAY_TIMER = 5000

  const [ref, isVisible] = useInView({
    threshold: 0.4,
    triggerOnce: true,
  })

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', '50vh'],
  })

  const yParallax = useParallax(scrollYProgress, [-200, 0])

  const [currentSlide, setCurrentSlide] = useState(0)
  const [details, setDetails] = useState<any>(null)
  const [opacities, setOpacities] = useState<number[]>([])
  const timer = useRef<any>()

  const [slider, instanceRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1800,
    },
    drag: false,
    loop: true,
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
    },
    breakpoints: {
      '(max-width: 1199px)': {
        drag: true,
      },
    },
  })

  useEffect(() => {
    timer.current = setInterval(() => {
      instanceRef.current?.next()
    }, AUTOPLAY_TIMER)

    return () => clearInterval(timer.current)
  }, [slider, instanceRef])

  return (
    <Container ref={ref}>
      <Head>
        {title ? <Title tag="h3" text={title} /> : null}

        {subtitle ? (
          <FadeInUp>
            <Subtitle>{subtitle}</Subtitle>
          </FadeInUp>
        ) : null}
      </Head>
      <AnimatedLine variant="light" />
      <AnimatePresence mode="wait">
        <DropDown>
          <Wrapper ref={containerRef}>
            <Slider ref={slider} style={{ y: yParallax }}>
              {images.map((item, index) => {
                return (
                  <Slide className="keen-slider__slide" key={index}>
                    <Inner style={positionStyle(instanceRef, details, index)}>
                      <Image {...item} />
                    </Inner>
                  </Slide>
                )
              })}
            </Slider>

            <Box>
              {description ? (
                <FadeInUp>
                  <Description
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </FadeInUp>
              ) : null}

              <Media greaterThanOrEqual="desktopSmall">
                {cta ? (
                  <CTA href={cta.URL} className="cta">
                    <ButtonText
                      viewBox="0 0 400 400"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <defs>
                        <path
                          id="circlePath"
                          d="M 200, 200 m -100, 0 a 100,100 0 1,1 200,0 a 100,100 0 1,1 -200,0"
                        ></path>
                      </defs>
                      <text textLength="600">
                        <textPath
                          href="#circlePath"
                          textLength="600"
                          startOffset="0"
                        >
                          {cta.label} {cta.label} {cta.label}
                        </textPath>
                      </text>
                    </ButtonText>

                    <Enter>
                      <ControlRight />
                    </Enter>
                  </CTA>
                ) : null}
              </Media>

              <Media lessThan="desktopSmall">
                {cta ? (
                  <MobileCTA
                    label={cta.label}
                    rel="noopener"
                    target="_blank"
                    URL={cta.URL}
                    variant="white"
                  />
                ) : null}
              </Media>
            </Box>

            {images.length > 1 ? (
              <Dots dial={6}>
                {images.map((_item, index) => (
                  <Dot
                    counter={`${currentSlide + 1}/${images.length}`}
                    key={index}
                    className={currentSlide === index ? 'active' : undefined}
                    onClick={() => {
                      instanceRef.current?.moveToIdx(index)
                    }}
                  />
                ))}
              </Dots>
            ) : null}
          </Wrapper>
        </DropDown>
      </AnimatePresence>
      <AnimatedLine variant="dark" />
    </Container>
  )
})

const Container = styled.section`
  margin-top: 9.75rem;
  text-align: center;

  > div:last-of-type {
    margin-top: 0.75rem;

    &::after {
      content: '';
      position: absolute;
      top: -0.8125rem;
      left: 0;
      height: 0.75rem;
      width: 100%;
      transition: none;

      background: ${({ theme }) => theme.colors.variants.neutralLight4};
    }
  }

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
  }
`

const Head = styled.div`
  max-width: 38.25rem;
  padding: 0 1.875rem;
  margin: 0 auto 3.25rem;

  @media (max-width: 1199px) {
    max-width: 20.875rem;
    margin-bottom: 2.25rem;
  }
`
const Title = styled(AnimatedTitle)`
  ${styles.title.large}

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Subtitle = styled.h6`
  ${styles.title.xs}

  margin-top: 0.75rem;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 0.375rem;
  }
`

const Wrapper = styled.div`
  width: 100%;
  aspect-ratio: 1.8;
  max-height: 50rem;
  overflow: hidden;
  position: relative;
  margin: 0.75rem 0 0;
  padding-bottom: 0.75rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight4};

  @media (max-width: 1199px) {
    aspect-ratio: 0.72;
    min-height: 32rem;
    max-height: none;
  }
`

const Slider = styled(motion.div)`
  display: flex;
  width: 100%;
  height: calc(100% + 12.5rem);
  outline: none;
  overflow: hidden;
  position: relative;

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.4;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }
`

const Slide = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  transition: none;
  transition-duration: 1500ms;
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition-timing-function: cubic-bezier(0.85, 0, 0.15, 1);

  & > div {
    height: 100%;
  }

  img {
    ${styles.img}
  }
`

const Box = styled.div`
  max-width: 24.5rem;
  position: absolute;
  top: 6.25rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;

  @media (max-width: 1199px) {
    width: 100%;
    max-width: unset;
    bottom: 0;
  }
`

const Description = styled.div`
  ${styles.description.light}

  position: relative;
  z-index: 1;

  @media (max-width: 1199px) {
    display: none;
  }
`

const Dots = styled(FlexBox)`
  gap: 0.375rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 5.25rem;
  z-index: 3;

  @media (max-width: 1199px) {
    right: 1.5rem;
  }
`

const ButtonText = styled.svg`
  width: 180%;
  height: 180%;
  position: absolute;
  top: -40%;
  left: -40%;
  border-radius: 50%;
  transform: rotate(-90deg);
  transition: 0.4s cubic-bezier(0.25, 1, 0.5, 1);

  text {
    font-family: ${({ theme }) => theme.fontFamily.heading};
    font-size: 1.125rem;
    letter-spacing: -0.4em;
    fill: ${({ theme }) => theme.colors.variants.neutralLight4};
    font-weight: 300;
    text-transform: uppercase;
  }
`

const Enter = styled.span`
  width: 8.25rem;
  height: 8.25rem;
  position: relative;

  svg {
    fill: ${({ theme }) => theme.colors.variants.neutralLight4};
    width: 1.875rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.primaryDark};
    border-radius: 50%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    transition: 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    transform: scale(0);
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const CTA = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 11.875rem;
  height: 11.875rem;
  transition: 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  margin-top: 3rem;
  transform: translateX(50%);

  &:hover {
    ${ButtonText} {
      transform: rotate(0);
    }

    ${Enter} {
      &:before {
        transform: scale(1);
      }
    }
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const MobileCTA = styled(Button)`
  font-family: ${({ theme }) => theme.fontFamily.heading};
  position: absolute;
  bottom: 5.25rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.6875rem 2.125rem 0.6875rem 2.125rem;
  letter-spacing: 0.05em;
`
